import { useEffect, useRef } from 'react';
import { useBoard, usePane, usePaneStore, useYMap } from '../context';
import type { BoardMetaSettings } from '../BoardSettings';
import { ItemView } from '../items/ItemView';
import { SelectionOverlay } from '../selection/SelectionOverlay';
import { lazy, Suspense } from 'react';
import { RICH_TYPES } from '../../doc/schema';
const FormatBar = lazy(() => import('../text/FormatBar').then((m) => ({ default: m.FormatBar })));
import { useToolSettings } from '../toolSettings';
import type { Camera } from './camera';
import { CutoutOverlay } from './CutoutOverlay';
import { DrawOverlay } from './DrawOverlay';
import { useGestures } from './useGestures';
import { useVisibleItemIds } from './useVisibleItems';


/** An eyedropper whose tip (bottom left) is the hot spot; outlined so it shows on any canvas. */
const PICKER_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="-1 -1 18 18" fill="none" stroke-linecap="square">' +
    '<g stroke="#fff" stroke-width="4"><path d="M9 6l-6 6-1 2 2-1 6-6M7.5 4.5l4 4M9.5 4.5L12 2a1.4 1.4 0 0 1 2 2l-2.5 2.5z"/></g>' +
    '<g stroke="#111" stroke-width="1.5"><path d="M9 6l-6 6-1 2 2-1 6-6M7.5 4.5l4 4"/><path d="M9.5 4.5L12 2a1.4 1.4 0 0 1 2 2l-2.5 2.5z" fill="#111"/></g>' +
    '</svg>',
)}") 2 18, crosshair`;

/** The pannable/zoomable surface. Camera changes are applied straight to the DOM, outside React. */
export function Viewport() {
  const ref = useRef<HTMLDivElement>(null);
  const worldRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const pane = usePaneStore();
  const board = useBoard();
  const meta = useYMap<unknown>(board.meta) as BoardMetaSettings;
  const gridStyle = meta.grid ?? 'dots';
  const gridWorld = meta.gridSize ?? 32;
  const bg = meta.bg ?? null;
  const handlers = useGestures(ref);
  const ids = useVisibleItemIds();
  const tool = usePane((s) => s.tool);
  const spaceHeld = usePane((s) => s.spaceHeld);
  const dragOver = usePane((s) => s.dragOver);
  const penMode = useToolSettings((s) => s.penMode);
  const editingRich = usePane((s) => {
    const it = s.editingId ? board.index.get(s.editingId) : undefined;
    return !!it && RICH_TYPES.has(it.type);
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => pane.getState().setSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pane]);

  useEffect(() => {
    const el = ref.current;
    const world = worldRef.current;
    const grid = gridRef.current;
    if (!el || !world || !grid) return;
    el.style.backgroundColor = bg ?? '';
    grid.style.backgroundImage =
      gridStyle === 'lines'
        ? 'linear-gradient(var(--canvas-dot) 1px, transparent 1px), linear-gradient(90deg, var(--canvas-dot) 1px, transparent 1px)'
        : gridStyle === 'none'
          ? 'none'
          : 'radial-gradient(var(--canvas-dot) 1px, transparent 1.5px)';
    let lastStep = 0;
    const apply = (c: Camera) => {
      world.style.transform = `translate(${c.x}px, ${c.y}px) scale(${c.zoom})`;
      if (gridStyle === 'none') return;
      // keep the grid readable: double the spacing until cells are at least 16px, halve above 96px
      let step = gridWorld * c.zoom;
      while (step < 16) step *= 2;
      while (step > 96) step /= 2;
      if (step !== lastStep) {
        grid.style.backgroundSize = `${step}px ${step}px`;
        lastStep = step;
      }
      // The grid layer is a composited element larger than the viewport; sliding it by the
      // camera offset modulo one cell is a transform-only change (no repaint each frame),
      // unlike animating background-position on the viewport itself.
      const ox = ((c.x % step) + step) % step - step;
      const oy = ((c.y % step) + step) % step - step;
      grid.style.transform = `translate(${ox}px, ${oy}px)`;
    };
    apply(pane.getState().camera);
    return pane.subscribe((s, prev) => {
      if (s.camera !== prev.camera) apply(s.camera);
    });
  }, [pane, bg, gridStyle, gridWorld]);

  const cursor =
    spaceHeld || tool === 'pan'
      ? 'grab'
      : tool === 'select'
        ? 'default'
        : tool === 'draw' && penMode === 'eraser'
          ? 'cell'
          : tool === 'draw' && penMode === 'picker'
            ? PICKER_CURSOR
            : 'crosshair';

  return (
    <div
      ref={ref}
      className={`viewport tool-${tool}${tool === 'draw' ? ` mode-${penMode}` : ''}${dragOver ? ' is-dragover' : ''}`}
      tabIndex={0}
      style={{ cursor }}
      {...handlers}
    >
      <div ref={gridRef} className="grid-layer" aria-hidden />
      <div ref={worldRef} className="world">
        {ids.map((id) => (
          <ItemView key={id} id={id} />
        ))}
      </div>
      <MarqueeOverlay />
      <CreateOverlay />
      <SnapGuides />
      <SelectionOverlay />
      {editingRich && (
        <Suspense fallback={null}>
          <FormatBar />
        </Suspense>
      )}
      <DrawOverlay />
      <CutoutOverlay />
    </div>
  );
}

function MarqueeOverlay() {
  const marquee = usePane((s) => s.marquee);
  const camera = usePane((s) => s.camera);
  if (!marquee) return null;
  return (
    <div
      className="marquee"
      style={{
        left: marquee.x * camera.zoom + camera.x,
        top: marquee.y * camera.zoom + camera.y,
        width: marquee.w * camera.zoom,
        height: marquee.h * camera.zoom,
      }}
    />
  );
}

function SnapGuides() {
  const g = usePane((s) => s.snapGuides);
  const camera = usePane((s) => s.camera);
  if (g.x === null && g.y === null) return null;
  return (
    <>
      {g.x !== null && <div className="snap-guide snap-guide-v" style={{ left: g.x * camera.zoom + camera.x }} />}
      {g.y !== null && <div className="snap-guide snap-guide-h" style={{ top: g.y * camera.zoom + camera.y }} />}
    </>
  );
}

function CreateOverlay() {
  const rect = usePane((s) => s.creating);
  const camera = usePane((s) => s.camera);
  if (!rect) return null;
  return (
    <div
      className="create-preview"
      style={{
        left: rect.x * camera.zoom + camera.x,
        top: rect.y * camera.zoom + camera.y,
        width: rect.w * camera.zoom,
        height: rect.h * camera.zoom,
      }}
    />
  );
}
